// src/components/Layout/ChatContainer.js (updated)
import React, { Suspense, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import LoadingSpinner from "../Animation/LoadingSpinner";
import MessageContent from "../VoiceBot/MessageContent";
import image from "../../assets/user.png";
import { useTranslation } from "../../hooks/useTranslation";
import { useLanguage } from "../../contexts/LanguageContext";
import { translateMessages } from "../../utils/translationService";

const Talk = React.lazy(() => import("../Talk/Talk"));
const Chat = React.lazy(() => import("../Chat/Chat"));

const ChatContainer = ({
  chatBoxRef,
  customBranding,
  messages,
  isTyping,
  isWaitingForResponse,
  config,
  toggleAudioPlayback,
  audioPlaying,
  handleToggleSpeech,
  playingMessageId,
  activeSection,
  setMessages,
  recordingState,
  setRecordingState,
  recordingDuration,
  setRecordingDuration,
  speechDetected,
  setSpeechDetected,
  setIsTyping,
  setIsLoading,
  setAbortController,
  playAudioAutomatically,
  showContactForm,
  setShowContactForm,
  showStatusForm,
  setShowStatusForm,
  showFeedbackForm,
  setShowFeedbackForm,
  showLoginForm,
  setShowLoginForm,
  currentChatId,
  isLoggedIn,
  setIsLoggedIn,
  setIsWaitingForResponse,
}) => {
  // Add state for logoUrl and greetingMessage
  const [logoUrl, setLogoUrl] = React.useState(image);
  const [greetingMessage, setGreetingMessage] = React.useState("");
  
  // Get current language for translation
  const { currentLanguage } = useLanguage();
  
  // Translate greeting message
  const { translated: translatedGreeting } = useTranslation(greetingMessage);

  React.useEffect(() => {
    // Get logo_url and greetingMessage from chatBotData
    const storedData = localStorage.getItem('chatBotData');
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        if (parsedData.logo_url) {
          setLogoUrl(parsedData.logo_url);
        } else {
          setLogoUrl(image);
        }
        setGreetingMessage(parsedData.greetingMessage || "How can I help you today?");
      } catch (error) {
        setLogoUrl(image);
        setGreetingMessage("How can I help you today?");
      }
    } else {
      setLogoUrl(image);
      setGreetingMessage("How can I help you today?");
    }
  }, []);

  // Translate all messages when language changes
  React.useEffect(() => {
    const translateAllMessages = async () => {
      if (messages.length > 0) {
        try {
          const translatedMessages = await translateMessages(messages, currentLanguage.code);
          // No need to update state as we'll render the translated versions in the component
          console.log('Messages translated successfully');
        } catch (error) {
          console.error('Error translating messages:', error);
        }
      }
    };

    translateAllMessages();
  }, [currentLanguage, messages]);

  // Helper to determine if this is AI chat
  const isAiChat = currentChatId && localStorage.getItem("aiChatMessages") &&
    JSON.parse(localStorage.getItem("aiChatMessages")).id === currentChatId;

  // Insert greeting message at the top if AI chat and not already present
  let displayMessages = messages.filter(msg => msg.content && msg.content.trim() !== "");
  if (isAiChat) {
    displayMessages = [
      {
        type: "bot",
        content: greetingMessage,
        timestamp: new Date(),
        isGreeting: true,
      },
      ...displayMessages.filter((msg) => !(msg.type === "bot" && msg.content === greetingMessage)),
    ];
  }

  return (
    <>
      {/* Chat messages area */}
      <div
        className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4"
        style={{
          backgroundColor: customBranding.chatBgColor,
        }}
        ref={chatBoxRef}
      >
        <AnimatePresence>
          {displayMessages.map((message, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -30 }}
              transition={{ type: "spring", stiffness: 100 }}
              className={`flex ${message.type === "user" ? "justify-end" : "justify-start"}`}
            >
              <div className={`flex ${message.type === "user" ? "flex-row-reverse" : "flex-col"} max-w-[80%] sm:max-w-[70%]`}>
                {message.type !== "user" && (
                  <img
                    src={logoUrl}
                    alt="AI Chat Logo"
                    className="w-[4rem] h-[2rem] object-contain rounded-full"
                    onError={e => { e.target.onerror = null; e.target.src = image; }}
                  />
                )}
                <div
                  className={`p-3 rounded-2xl shadow-md break-words text-sm sm:text-base
                    ${message.isGreeting ? "mt-0" : "mt-2"}
                  `}
                  style={{
                    backgroundColor: message.type === "user"
                      ? customBranding.msgUserBgColor
                      : customBranding.msgBotBgColor,
                    color: message.type === "user"
                      ? customBranding.msgUserTextColor
                      : customBranding.msgBotTextColor,
                  }}
                >
                  <MessageContent
                    message={message}
                    config={config}
                    customBranding={customBranding}
                    toggleAudioPlayback={toggleAudioPlayback}
                    audioPlaying={audioPlaying}
                    handleToggleSpeech={handleToggleSpeech}
                    playingMessageId={playingMessageId}
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {(isTyping || isWaitingForResponse) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center text-gray-500 dark:text-gray-300"
          >
            <img
              src={logoUrl}
              alt="AI Chat Logo"
              className="w-8 h-8 mr-3 object-contain rounded-full"
              onError={e => { e.target.onerror = null; e.target.src = image; }}
            />
            <LoadingSpinner />
          </motion.div>
        )}
      </div>
      {/* Input area */}
      <div
        className="p-2 shadow-top pb-0"
        style={{
          backgroundColor: customBranding.chatBgColor,
        }}
      >
        <Suspense fallback={<div>Loading...</div>}>
          {activeSection === "talk" && (
            <Talk
              setMessages={setMessages}
              isWaitingForResponse={isWaitingForResponse}
              setIsWaitingForResponse={setIsWaitingForResponse}
              recordingState={recordingState}
              setRecordingState={setRecordingState}
              recordingDuration={recordingDuration}
              setRecordingDuration={setRecordingDuration}
              speechDetected={speechDetected}
              setSpeechDetected={setSpeechDetected}
              setIsTyping={setIsTyping}
              setIsLoading={setIsLoading}
              setAbortController={setAbortController}
              playingMessageId={playingMessageId}
              handleToggleSpeech={handleToggleSpeech}
            />
          )}
          {activeSection === "chat" && (
            <Chat
              setMessages={setMessages}
              isWaitingForResponse={isWaitingForResponse}
              setIsWaitingForResponse={setIsWaitingForResponse}
              setIsTyping={setIsTyping}
              setAbortController={setAbortController}
              showContactForm={showContactForm}
              setShowContactForm={setShowContactForm}
              showStatusForm={showStatusForm}
              setShowStatusForm={setShowStatusForm}
              showFeedbackForm={showFeedbackForm}
              setShowFeedbackForm={setShowFeedbackForm}
              showLoginForm={showLoginForm}
              setShowLoginForm={setShowLoginForm}
              currentChatId={currentChatId}
              isLoggedIn={isLoggedIn}
              setIsLoggedIn={setIsLoggedIn}
              isFromLogin={true}
            />
          )}
        </Suspense>
      </div>
    </>
  );
};

export default ChatContainer; 